import { t } from '../../../locales';
import { featureFlag, isFeatureFlagEnabled } from '../../entities/feature-flag';
import { useAuthStore } from '../../store/shared/auth-store';
import { pluginRegistry } from './plugins/registry';
import { usePluginsStore } from './plugins/store';

// Legacy tenants (without KNOWLEDGE_BASE_V2) should not be able to reach the new
// knowledge base via direct URL. Send them to the legacy FAQ screen that matches
// their Firebase `faqsV2Enabled` config.
const redirectNewKbIfLegacy = (to, from, next) => {
	if (isFeatureFlagEnabled(featureFlag.KNOWLEDGE_BASE_V2)) {
		next();
		return;
	}
	const authStore = useAuthStore();
	const target = authStore.firebaseTenantConfig?.faqsV2Enabled
		? '/v2/common/faqs-v2'
		: '/v2/common/faqs';
	next(target);
};

const waitForHydration = (store) =>
	new Promise((resolve) => {
		if (store.hydrated) return resolve();
		const unsub = store.$subscribe(() => {
			if (store.hydrated) {
				unsub();
				resolve();
			}
		});
	});

const redirectIfPluginDisabled = (pluginId) => async (to, from, next) => {
	const pluginsStore = usePluginsStore();
	const subscribed = pluginsStore.subscribe();
	if (!subscribed) return next('/v2/linker/plugins');
	await waitForHydration(pluginsStore);
	next(pluginsStore.isPluginReady(pluginId) ? undefined : '/v2/linker/plugins');
};

const pluginChildren = pluginRegistry.flatMap((plugin) =>
	plugin.routes.map((route) => ({
		...route,
		beforeEnter: redirectIfPluginDisabled(plugin.id),
		meta: { title: t(route.titleKey), pluginId: plugin.id, featureFlag: featureFlag.CUSTOMER_SERVICE },
	})),
);

const linkerAgentRoutes = {
	path: 'linker',
	component: () => import('./module-layout.vue'),
	meta: {
		featureFlag: featureFlag.LINKER_AGENT,
	},
	children: [
		{
			path: 'plugins',
			component: () => import('./pages/skills/index.vue'),
			meta: {
				title: t('linker.plugins.title'),
				featureFlag: featureFlag.CUSTOMER_SERVICE,
			},
		},
		...pluginChildren,
		{
			path: 'knowledge-base',
			redirect: '/v2/linker/knowledge-base/websites',
			meta: {
				featureFlag: featureFlag.AI_AGENT,
			},
		},
		{
			path: 'knowledge-base/websites',
			component: () => import('./pages/knowledge-base/websites.vue'),
			beforeEnter: redirectNewKbIfLegacy,
			meta: {
				title: t('linker.urls'),
				featureFlag: featureFlag.AI_AGENT,
			},
		},
		{
			path: 'knowledge-base/documents',
			component: () => import('./pages/knowledge-base/documents.vue'),
			beforeEnter: redirectNewKbIfLegacy,
			meta: {
				title: t('linker.files'),
				featureFlag: featureFlag.AI_AGENT,
			},
		},
		{
			path: 'knowledge-base/qa',
			component: () => import('./pages/knowledge-base/qa.vue'),
			beforeEnter: redirectNewKbIfLegacy,
			meta: {
				title: t('linker.faqs'),
				featureFlag: featureFlag.AI_AGENT,
			},
		},
		{
			path: 'knowledge-base/:sourceId',
			component: () => import('./pages/knowledge-base/detail.vue'),
			beforeEnter: redirectNewKbIfLegacy,
			meta: {
				title: t('linker.knowledge-base'),
				featureFlag: featureFlag.AI_AGENT,
			},
		},
	],
};

export { linkerAgentRoutes };
